/**
 * captureAttribution — wrapper cliente para enviar eventos a
 * /api/track-attribution-event con la atribución del primer pageview
 * adjunta automáticamente.
 *
 * Cero acoplamiento con PostHog — esta función ES exclusiva para nuestro
 * tracking de growth (Slack real-time + admin dashboard). Si querés
 * trackear el mismo evento en PostHog, llamalo también:
 *
 *   captureAttribution("hero_cta_clicked", { template, heroCtaType });
 *   posthog.capture("hero_cta_clicked", { template, heroCtaType });
 *
 * Best-effort: si DB / network falla, no rompe UX. fire-and-forget con
 * keepalive: true para que sobreviva navegaciones.
 *
 * Conventions:
 *   - Llamar SIEMPRE con referrer + landingUrl del primer pageview
 *     (los provee getFirstVisitAttribution automáticamente)
 *   - device se auto-detecta del UA si no se pasa explícito
 *   - country/language quedan opcionales — los completa el backend si
 *     vienen de un endpoint con geo guard.
 */

import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { getFirstVisitAttribution } from "@/lib/firstVisitAttribution";
import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";
import {
  getClientContextPropertiesPostHog,
  getPostHogIdentityPropertiesPostHog,
  getPostHogSlackSessionPropertiesPostHog,
} from "@/lib/posthogAnalytics";
import {
  FUNNEL_STAGE,
  type AttributionEventName,
} from "@shared/attributionFunnel";

export type { AttributionEventName } from "@shared/attributionFunnel";

export interface CaptureAttributionOptions {
  sessionId?: string;
  distinctId?: string;
  /** Path actual cuando dispara — default: window.location.pathname. */
  path?: string;
  country?: string;
  language?: string;
  template?: string;
  heroCtaType?: string;
  /** Para conversions: monto en cents. */
  valueCents?: number;
  currency?: string;
  /** Params específicos del evento (variant id, plan tier, etc.). */
  metadata?: Record<string, unknown>;
}

function detectDevice(): "mobile" | "tablet" | "desktop" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobile|iphone|android|opera mini|iemobile/.test(ua)) return "mobile";
  if (/windows|macintosh|linux|x11/.test(ua)) return "desktop";
  return "unknown";
}

function detectLanguage(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const html = document.documentElement.lang;
  if (html) return html.slice(0, 16);
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language.slice(0, 16);
  }
  return undefined;
}

type PostHogClient = {
  capture?: (event: string, props: Record<string, unknown>) => void;
  get_distinct_id?: () => string;
  get_session_id?: () => string;
  get_session_replay_url?: () => string;
};

function getPostHogClient(): PostHogClient | null {
  if (typeof window === "undefined") return null;
  const ph = (window as unknown as { posthog?: PostHogClient }).posthog;
  return ph?.capture ? ph : null;
}

/** Best-effort mirror → PostHog para funnels unificados (mismo event_name que attribution_events). */
function mirrorAttributionToPostHog(
  eventName: AttributionEventName,
  options: CaptureAttributionOptions,
  body: {
    referrer: string;
    landingUrl: string;
    path: string;
    language?: string;
    device: ReturnType<typeof detectDevice>;
  },
): void {
  const ph = getPostHogClient();
  if (!ph?.capture) return;

  try {
    const traffic = detectTrafficSource();
    const phDistinct = options.distinctId ?? ph.get_distinct_id?.() ?? undefined;
    const phSession = options.sessionId ?? ph.get_session_id?.() ?? undefined;
    const replayUrl = ph.get_session_replay_url?.() ?? undefined;

    const revenue =
      options.valueCents != null ? options.valueCents / 100 : undefined;

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    ph.capture(eventName, {
      path: body.path,
      page_path: body.path,
      page_url: typeof window !== "undefined" ? window.location.href : undefined,
      referrer: body.referrer || undefined,
      landing_url: body.landingUrl || undefined,
      country_code: options.country,
      language: body.language,
      template: options.template,
      hero_cta_type: options.heroCtaType,
      device: body.device,
      user_agent: ua ? ua.slice(0, 300) : undefined,
      value_cents: options.valueCents,
      revenue,
      currency: options.currency?.toUpperCase(),
      traffic_source: traffic.source,
      traffic_medium: traffic.medium,
      traffic_channel: traffic.channel,
      in_app_browser: traffic.inAppBrowser,
      funnel_stage: FUNNEL_STAGE[eventName],
      funnel_event: eventName,
      ph_session_id: phSession,
      ph_distinct_id: phDistinct,
      ph_session_replay_url: replayUrl,
      attribution_mirror: true,
      attribution_mirror_source: "client",
      timestamp: new Date().toISOString(),
      ...getWorkerGeoAnalyticsProps(),
      ...getClientContextPropertiesPostHog(),
      ...getPostHogIdentityPropertiesPostHog(),
      ...getPostHogSlackSessionPropertiesPostHog(),
      ...(options.metadata ?? {}),
    });

    ph.capture("conversion_funnel_step", {
      funnel_step: eventName,
      funnel_stage: FUNNEL_STAGE[eventName],
      path: body.path,
      template: options.template,
      country_code: options.country,
      device: body.device,
      traffic_source: traffic.source,
      traffic_medium: traffic.medium,
      ph_session_id: phSession,
      ph_distinct_id: phDistinct,
      attribution_mirror: true,
      ...(options.metadata ?? {}),
    });
  } catch {
    /* silent — best-effort */
  }
}

/**
 * Captura un evento de attribution. Fire-and-forget — no devolvemos
 * promesa porque el caller típicamente no espera. Si querés esperar el
 * eventId (para correlacionar logs), usá captureAttributionAwait.
 */
export function captureAttribution(
  eventName: AttributionEventName,
  options: CaptureAttributionOptions = {},
): void {
  void captureAttributionAwait(eventName, options).catch(() => {
    /* silent — best-effort, ya logueamos del lado server */
  });
}

export async function captureAttributionAwait(
  eventName: AttributionEventName,
  options: CaptureAttributionOptions = {},
): Promise<{ ok: boolean; persisted?: boolean; eventId?: number; isNewSource?: boolean } | null> {
  if (typeof window === "undefined") return null;

  const attribution = getFirstVisitAttribution();
  const path = options.path ?? window.location.pathname;
  const language = options.language ?? detectLanguage();
  const device = detectDevice();
  const body = {
    eventName,
    sessionId: options.sessionId,
    distinctId: options.distinctId,
    referrer: attribution.referrer,
    landingUrl: attribution.landingUrl,
    path,
    country: options.country,
    language,
    template: options.template,
    heroCtaType: options.heroCtaType,
    device,
    valueCents: options.valueCents,
    currency: options.currency,
    metadata: options.metadata,
  };

  mirrorAttributionToPostHog(eventName, options, {
    referrer: attribution.referrer,
    landingUrl: attribution.landingUrl,
    path,
    language,
    device,
  });

  try {
    const res = await fetch("/api/track-attribution-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    return data as { ok: boolean; persisted?: boolean; eventId?: number; isNewSource?: boolean } | null;
  } catch {
    return null;
  }
}
